/**
 * What says a CONCRETE emplacement is manned, before it fires (ROUND4 §3). Bullets bounce off a
 * bunker, so the player has to be able to read "there is a gunner in there" at a glance - and read
 * "this one is cleared" just as plainly. Two parts, both drawn by `world/Emplacements`:
 *
 *  - the `gun`, built about `pivot` in the prop's own frame and turned to the gunner's facing. It is
 *    NOT part of the prop's model: an empty emplacement has no barrel at all, which is the tell.
 *  - the `slot`, a warm bar just proud of the embrasure, drawn self-lit while somebody is behind it.
 *
 * Everything faces +z (south) at yaw 0, as every other kit model does.
 */

import type { PropKind } from '../../sim/types';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

/** How far a gun in a slot may swing either side of the emplacement's heading: the embrasure's own arc. */
const SLOT_ARC = 0.7;
/** A turret traverses all round (02 §9.2, "360 degrees, slow traverse"). */
const FULL_ARC = Math.PI;

/** Colour of a lit embrasure: the dull warm glow of a fighting compartment, not a lamp. */
const SLOT_COLOUR = 'fire.1';

export interface EmplacementTell {
  /** Where the gun turns about, in the prop's frame: a ball mount in the wall, or a turret's axis. */
  pivot: readonly [x: number, y: number, z: number];
  /** The barrel, built about `pivot`, muzzle to +z. Null: the kind's own model already carries its gun. */
  gun: KitBuilder | null;
  /** The lit part of the embrasure, in the prop's frame. It does not turn with the gun. */
  slot: KitBuilder;
  /** Radians either side of the prop's yaw the barrel may swing. */
  arc: number;
}

/**
 * Bunker gun: a ball mount filling the slot, a jacketed barrel and a muzzle. Modelled about the
 * mount, so whatever the traverse the whole barrel is outside the wall - never sunk into concrete.
 */
const bunkerGun: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const steel = { all: p.color('metal.lo'), top: p.color('metal.mid') };
  mb.box(0.52, 0.36, 0.3, { all: p.color('metal.mid'), top: p.color('metal.hi') }, place(0, 0, 0.04), true);
  mb.box(0.26, 0.26, 0.58, steel, place(0, 0, 0.44), true);
  mb.box(0.16, 0.16, 0.82, steel, place(0, 0, 1.03), true);
  mb.box(0.22, 0.22, 0.18, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(0, 0, 1.5), true);
  return mb;
};

/** The bunker's slot, lit: a bar across the embrasure, just proud of its dark inset. */
const bunkerSlot: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(2.6, 0.2, 0.05, p.color(SLOT_COLOUR), place(0, 0.98, 2.07));
  return mb;
};

/** Turret gun: the mantlet and barrel that used to be part of the drum, now the crew's own. */
const turretBunkerGun: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.7, 0.42, 0.5, p.color('metal.lo'), place(0, 1.68, 1.05));
  // Long enough to stand clear of the roof ring (its flat face is 2.19 m out) whatever the traverse.
  mb.box(0.2, 0.2, 1.5, { all: p.color('metal.lo'), top: p.color('metal.mid') }, place(0, 1.8, 2.1));
  mb.box(0.3, 0.3, 0.22, { all: p.color('ink.1'), top: p.color('metal.lo') }, place(0, 1.75, 2.75));
  return mb;
};

/** The turret bunker's three slits, lit: the drum glows all round, whichever way the gun is pointing. */
const turretBunkerSlot: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const yaw of [-0.628, 0, 0.628]) {
    mb.with(place(0, 0, 0, yaw), () => mb.box(0.8, 0.17, 0.05, p.color(SLOT_COLOUR), place(0, 0.84, 2.02)));
  }
  return mb;
};

/** The casemate keeps its coastal gun whoever is inside; only the embrasure lights up. */
const casemateSlot: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (const sx of [-1, 1]) {
    mb.box(0.62, 0.34, 0.05, p.color(SLOT_COLOUR), place(sx * 0.6, 1.28, 2.82));
  }
  return mb;
};

/**
 * The concrete kinds a soldier can `mans` (sim/enemies/specials). The open ones - the MG nest, the
 * gun pit, the manned turret - need nothing: the gunner himself is plainly in them.
 */
export const EMPLACEMENT_TELLS: Partial<Record<PropKind, EmplacementTell>> = {
  bunker: { pivot: [0, 1.09, 1.9], gun: bunkerGun, slot: bunkerSlot, arc: SLOT_ARC },
  turretBunker: { pivot: [0, 0, 0], gun: turretBunkerGun, slot: turretBunkerSlot, arc: FULL_ARC },
  casemate: { pivot: [0, 0, 0], gun: null, slot: casemateSlot, arc: SLOT_ARC },
};

/** True for a prop kind whose model alone cannot say whether anybody is inside it. */
export function hasEmplacementTell(kind: PropKind): boolean {
  return EMPLACEMENT_TELLS[kind] !== undefined;
}
